import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { DiscountBox } from "./DiscountBox";
import { discountCalc } from "./discount";

const subtotalOf = (order) =>
  order.subtotal_original ??
  (order.items || []).reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 1), 0);

export function DiscountModal({ order, onClose, onSaved }) {
  const [type, setType] = useState(order.discount_type || "PERCENTUAL");
  const [value, setValue] = useState(
    order.discount_value ? String(order.discount_value) : ""
  );
  const [busy, setBusy] = useState(false);
  const subtotal = subtotalOf(order);
  const calc = discountCalc(subtotal, type, value);

  const save = async (clear = false) => {
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/discount`, {
        discount_type: clear ? null : type,
        discount_value: clear ? 0 : Number(value) || 0,
      });
      toast.success(clear ? "Desconto removido." : "Desconto aplicado ao pedido.");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Desconto do pedido ${order.number || ""}`}
      description="O desconto vale só para este pedido. Nenhum preço de produto, kit, combinação ou consignado é alterado."
      testid="order-discount-modal"
    >
      <div className="mt-2 space-y-4">
        <DiscountBox
          subtotal={subtotal}
          type={type}
          value={value}
          onType={setType}
          onValue={setValue}
          testid="order-discount"
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            onClick={() => save(false)}
            disabled={busy || !!calc.error}
            className="h-11 flex-1 gap-2 rounded-full font-semibold"
            data-testid="order-discount-save"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Aplicar desconto
          </Button>
          {order.discount_amount > 0 && (
            <Button
              variant="outline"
              onClick={() => save(true)}
              disabled={busy}
              className="h-11 rounded-full text-xs"
              data-testid="order-discount-clear"
            >
              Remover desconto
            </Button>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
}
